import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from 'antd'
import PerText from '../../../components/custom_components/Text'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { KudosDto, KudosFilterInput } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { KudosDetailsModal } from '../../../components/custom_components/KudosDetailsModal/KudosDetailsModal'
import { DeleteKudosModal } from '../../../components/custom_components/DeleteKudosModal/DeleteKudosModal'
import { useDateTime } from '../../../shared/useDateTime'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { ApolloError } from '@apollo/client'
import classNames from 'classnames'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'

export enum KudosDashboardView {
  replyer = 'replyer',
  managers = 'managers',
  hr = 'hr',
}

interface KudosTableProps {
  kudos?: KudosDto[]
  loading: boolean
  isLoadingMore: boolean
  error?: ApolloError
  filters: KudosFilterInput
  showMoreButtonVisible: boolean
  kudosDashboardView: KudosDashboardView
}

export const KudosTable: FC<KudosTableProps> = ({
  kudos,
  loading,
  isLoadingMore,
  error,
  filters,
  showMoreButtonVisible,
  kudosDashboardView,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [kudosToDelete, setKudosToDelete] = useState<KudosDto | null>(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [kudosToView, setKudosToView] = useState<KudosDto | null>(null)

  if ((loading && !isLoadingMore) || error || !kudos) {
    return (
      <BackgroundBox
        r
        height={
          kudosDashboardView === KudosDashboardView.replyer
            ? 'withReplyerKudosInfoBox'
            : kudosDashboardView === KudosDashboardView.hr
            ? 'withKudosInfoBoxAndTabs'
            : 'withKudosInfoBox'
        }
      >
        <NewLoadingAndErrors isLoading={loading && !isLoadingMore} error={error} hasNoData={!kudos} showSpinner t={t} />
      </BackgroundBox>
    )
  }

  const handleDeleteClick = (kudos: KudosDto) => {
    setKudosToDelete(kudos)
    setDeleteModalVisible(true)
  }

  const handleDeleteClose = () => {
    setDeleteModalVisible(false)
    setKudosToDelete(null)
  }

  const handleViewClick = (kudos: KudosDto) => {
    setKudosToView(kudos)
    setViewModalVisible(true)
  }

  const handleViewModalClose = () => {
    setViewModalVisible(false)
    setKudosToView(null)
  }

  const columns = [
    {
      title: t('kudos.table.employee'),
      key: 'employee',
      width: '20%',
      render: (record: KudosDto) => {
        const employee = record.employee
        if (!employee) return '-'

        return (
          <PerAvatarPlus
            firstName={employee.firstName}
            lastName={employee.lastName}
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            infoText={employee.companyName}
            nameType={record.employeeId === user.employeeId ? 'you' : 'fullName'}
          />
        )
      },
    },
    {
      title: t('kudos.table.description'),
      dataIndex: 'description',
      key: 'description',
      width: '40%',
      ellipsis: true,
      render: (description: string) => (
        <div className="overflow--hidden">
          <PerText fontSize="m" className="ellipsis--line-2">
            {description}
          </PerText>
        </div>
      ),
    },
    {
      title: t('kudos.table.author'),
      key: 'author',
      width: '20%',
      render: (record: KudosDto) => {
        const author = record.author
        if (!author) return '-'

        return (
          <PerAvatarPlus
            firstName={author.firstName}
            lastName={author.lastName}
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            infoText={author.companyName}
            nameType={record.authorId === user.employeeId ? 'you' : 'fullName'}
          />
        )
      },
    },
    {
      title: t('kudos.table.date'),
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: '9%',
      render: (date: string) => <PerText fontSize="xs">{formatDateShort(date)}</PerText>,
    },
    {
      title: t('kudos.table.actions'),
      key: 'actions',
      width: '11%',
      className: 'textAlign-right',
      render: (record: KudosDto) => (
        <PerTableActionButtons
          variant="pastel"
          buttons={[
            {
              icon: 'delete_fill',
              showOnlyWhen: record.deletable,
              color: 'error',
              tooltip: t('kudos.table.delete'),
              onClick: () => handleDeleteClick(record),
            },
            {
              icon: 'view_fill',
              color: 'primary',
              tooltip: t('kudos.table.view'),
              onClick: () => handleViewClick(record),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <Table
        className={classNames('light small scroll-compensation', {
          viewKudosHRTableAndShowMoreButton: showMoreButtonVisible && kudosDashboardView === KudosDashboardView.hr,
          viewKudosHRTable: !showMoreButtonVisible && kudosDashboardView === KudosDashboardView.hr,
          viewKudosTableAndShowMoreButton: showMoreButtonVisible && kudosDashboardView === KudosDashboardView.managers,
          viewKudosTable: !showMoreButtonVisible && kudosDashboardView === KudosDashboardView.managers,
          viewKudosReplyerTableAndShowMoreButton: showMoreButtonVisible && kudosDashboardView === KudosDashboardView.replyer,
          viewKudosReplyerTable: !showMoreButtonVisible && kudosDashboardView === KudosDashboardView.replyer,
        })}
        columns={columns}
        dataSource={kudos.map((kudos) => ({ ...kudos, key: kudos.id }))}
        pagination={false}
        rowKey="id"
        rowClassName={() => 'minHeight-row'}
        scroll={{ y: true }}
        locale={{
          emptyText: <PerEmpty heading={t('kudos.table.noKudosWithFilters')} info={t('kudos.table.noKudosWithFiltersInfo')} icon />,
        }}
      />

      <DeleteKudosModal
        visible={deleteModalVisible}
        onClose={handleDeleteClose}
        kudos={kudosToDelete}
        filters={filters}
        replyerView={kudosDashboardView === KudosDashboardView.replyer}
      />

      {kudosToView && <KudosDetailsModal visible={viewModalVisible} onClose={handleViewModalClose} kudos={kudosToView} />}
    </>
  )
}
